import { Icon, usePrompt } from '@ur-solutions/react-components'
import type { CellProps } from '@/components_deprecated'
import { Button, Cell } from '@/components_deprecated'
import { parseISO } from 'date-fns'
import { useDietDayMutations } from '@/modules/activities/mutations.hooks'
import type {
  ActivitiesDynamicColumnDayReport,
  DriverDietNode,
} from '@/modules/activities/types.graphql'
import type { MouseEventHandler } from 'react'
import {
  format,
  PERMISSIONS,
  useModuleConfig,
  useParam,
  usePermissions,
} from '@/utils'
import type { DriverDietForm } from './DriverDietPrompt'
import { DriverDietPrompt } from './DriverDietPrompt'
import { Module, ModuleOptionsValue } from '@/modules/companies'
import { useTranslate } from '@ur-solutions/react-hooks'
import styled from 'styled-components'

export const ElipsedText = styled.div<{ restrictWidth?: boolean }>`
  cursor: pointer;
  ${props =>
    props.restrictWidth &&
    `
    width: 36px;
    text-overflow: ellipsis;
    overflow: hidden;
    display: inline-block;
    vertical-align: bottom;
    white-space: nowrap;
  `}
`

const PartOfDayMapping: Record<number, string> = {
  1: '1',
  0.666666: '2/3',
  0.333333: '1/3',
}

interface DietProps {
  diet: Pick<
    DriverDietNode,
    'partOfDay' | 'salaryCode' | 'isManuallyEdited'
  > | null
  onClick: MouseEventHandler<HTMLButtonElement | HTMLDivElement>
  canEdit: boolean | null
  restrictWidth?: boolean
}
export const Diet: React.JSX.CFC<DietProps> = ({
  diet,
  canEdit,
  restrictWidth,
  ...props
}) => {
  const translations = useTranslate({
    manuallyEditedTitle: 'activities.diet-manually-edited-title',
  })
  if (diet && diet.salaryCode && diet.partOfDay > 0) {
    const isManuallyEdited = diet.isManuallyEdited
    const title = isManuallyEdited ? translations.manuallyEditedTitle : ''
    if (Object.keys(PartOfDayMapping).includes(diet.partOfDay.toString()))
      return (
        <div onClick={evt => props.onClick?.(evt)} title={title}>
          <ElipsedText restrictWidth={restrictWidth}>
            {isManuallyEdited && '*'}
            {PartOfDayMapping[diet.partOfDay]} {diet.salaryCode.name}
          </ElipsedText>
        </div>
      )
    else if (diet.partOfDay > 0)
      return (
        <div onClick={evt => props.onClick?.(evt)} title={title}>
          <ElipsedText restrictWidth={restrictWidth}>
            {isManuallyEdited && '*'}
            {diet.partOfDay} {diet.salaryCode.name}
          </ElipsedText>
        </div>
      )
  }

  if (canEdit)
    return (
      <Button
        padding="0.25rem 0.75rem !important"
        height="auto"
        onClick={evt => {
          evt.stopPropagation()
          props.onClick?.(evt)
        }}
      >
        <Icon icon="plus" margin="0" />
      </Button>
    )

  return <div onClick={evt => props.onClick?.(evt)}></div>
}

interface DietCellProps extends CellProps {
  report: Pick<ActivitiesDynamicColumnDayReport, 'diet' | 'date'>
  subColumn: boolean
  onClick?: () => void
}
export const DietCell: React.FC<DietCellProps> = ({
  narrow = true,
  centered = true,
  subColumn = false,
  report,
}) => {
  const userId = useParam('userId')
  const addPrompt = usePrompt()

  const { hasPermissions, hasPermissionsAndMe } = usePermissions()
  const { getModuleOptionStatus } = useModuleConfig(Module.ACTIVITIES)
  const simpleDietViewActive = getModuleOptionStatus(
    ModuleOptionsValue.ACTIVITIES_SIMPLE_DIET_OPTION
  )
  const commentMandatory = getModuleOptionStatus(
    ModuleOptionsValue.ACTIVITIES_DIET_COMMENT_MANDATORY
  )
  const mutations = useDietDayMutations()

  if (subColumn) return <td></td>

  const canEdit =
    hasPermissions(PERMISSIONS.activities.change.driveractivity, true) ||
    hasPermissionsAndMe(
      { id: userId },
      PERMISSIONS.activities.approve.driveractivity
    )

  async function handleAddDriverDiet() {
    const { data } = await addPrompt<DriverDietForm | null>(resolve => (
      <DriverDietPrompt
        initialData={{
          userId: userId,
          salaryCodeId: report.diet?.salaryCode?.id ?? null,
          partOfDay: report.diet?.partOfDay ?? 1,
          comment: report.diet?.comment ?? '',
        }}
        simpleView={simpleDietViewActive}
        commentMandatory={commentMandatory}
        edit={!!report.diet}
        onSubmit={resolve}
      />
    ))
    if (data === null) return

    void mutations.updateOrCreateDriverDiet({
      variables: {
        input: {
          ...data,
          date: format(parseISO(report.date), 'yyyy-MM-dd'),
        },
      },
    })
  }

  const handleClick: MouseEventHandler<HTMLTableCellElement> =
    report.diet && report.diet.salaryCode && canEdit
      ? event => {
          event.stopPropagation()
          void handleAddDriverDiet()
        }
      : _event => void 0

  return (
    <Cell onClick={handleClick} narrow={narrow} centered={centered}>
      <Diet
        onClick={handleAddDriverDiet}
        diet={report.diet}
        canEdit={canEdit}
      />
    </Cell>
  )
}
